import React, { PropsWithChildren } from "react";
import { ButtonBaseProps, Tooltip, TooltipProps } from "@mui/material";
import { Box } from "@mui/material";
import {
  Button,
  Container,
  IconContainer,
  LabelContainer,
  StyledBox,
  StyledCard,
  StyledTypography,
} from "./styled";
import { Link } from "react-router-dom";

type IconCardButtonProps = Omit<ButtonBaseProps, "ref"> & {
  label: string;
  icon?: React.JSX.Element;
  className?: string;
  href?: string;
  borderless?: boolean;
  disabled?: boolean;
  previous?: string;
  testId?: string;
};

const WithSurroundingTooltip = ({
  tooltip,
  children,
}: PropsWithChildren<{
  tooltip?: string;
  children: TooltipProps["children"];
}>) =>
  tooltip ? (
    <Tooltip title={tooltip} arrow>
      {children}
    </Tooltip>
  ) : (
    children
  );

const ChooseBorderless = ({
  borderless,
  children,
  label,
  href,
  previous,
  testId,
}: PropsWithChildren<{
  borderless?: boolean;
  children: React.ReactElement;
  label?: string;
  href?: string;
  previous?: string;
  testId?: string;
}>) => {
  return borderless ? (
    <WithSurroundingTooltip tooltip={label}>
      <StyledBox>{children}</StyledBox>
    </WithSurroundingTooltip>
  ) : (
    <WithSurroundingTooltip tooltip={label}>
      {href ? (
        <Link to={href} state={{ previous }}>
          <Button data-testid={testId}>{children}</Button>
        </Link>
      ) : (
        <Button disabled data-testid={testId}>
          {children}
        </Button>
      )}
    </WithSurroundingTooltip>
  );
};
const IconCardButton = ({
  ref,
  label,
  icon,
  className = "",
  borderless = false,
  href,
  previous,
  testId,
}: IconCardButtonProps & {
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <Container className={className} ref={ref}>
    <StyledCard borderless={borderless}>
      <ChooseBorderless
        borderless={borderless}
        label={label}
        href={href}
        previous={previous}
        testId={testId}
      >
        <>
          <IconContainer>{icon}</IconContainer>
          <LabelContainer>
            <Box>
              <StyledTypography size="medium" color="text.secondary">
                {label}
              </StyledTypography>
            </Box>
          </LabelContainer>
        </>
      </ChooseBorderless>
    </StyledCard>
  </Container>
);

const WithTooltip = ({
  tooltip = false,
  ...props
}: IconCardButtonProps & { tooltip?: boolean }) => (
  <IconCardButton {...props} />
);

export default WithTooltip;
